/**
 * ClassifyBookingDialog — web.
 *
 * Wraps POST /api/booked-trips/from-booking:
 *   { notification_id, trip_type: "pleasure"|"business", start_date, end_date, total_budget }
 *
 * Opened from NotificationsPage when a `booking_to_classify` notif is tapped.
 * On success the notification's classified_trip_id is populated server-side
 * and the parent refetches.
 */
import React, { useEffect, useState } from "react";
import { Compass, Briefcase, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ClassifyBookingDialog({ open, trip, onClose, onClassified }) {
  const [tripType, setTripType] = useState("pleasure");
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const t = todayISO();
      setTripType("pleasure");
      setStartDate(t);
      setEndDate(t);
      setBudget("");
    }
  }, [open]);

  if (!open || !trip) return null;

  const onSubmit = async () => {
    if (!startDate) { toast.error("Start date is required"); return; }
    setSaving(true);
    try {
      await api.post("/booked-trips/from-booking", {
        notification_id: trip.notification_id,
        trip_type: tripType,
        start_date: startDate,
        end_date: endDate || startDate,
        total_budget: budget ? parseFloat(budget) : 0,
      });
      toast.success(tripType === "pleasure"
        ? "Filed into your travel journal"
        : "Filed to Business trips");
      onClassified?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't file booking");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="classify-booking-dialog"
      className="fixed inset-0 z-50 bg-tb-navy-deep/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-tb-cream text-tb-navy rounded-2xl max-w-md w-full border-2 border-tb-gold shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {trip.image ? (
          <img src={trip.image} alt="" className="w-full h-40 object-cover rounded-t-2xl" />
        ) : null}
        <div className="p-6">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="font-serif-it text-2xl">Was this a pleasure or business trip?</h2>
              <p className="font-hand text-tb-navy/60 text-sm mt-1">
                {trip.destination_name || "Recent booking"}
                {trip.country ? ` · ${trip.country}` : ""}
              </p>
            </div>
            <button
              data-testid="classify-booking-close"
              onClick={onClose}
              className="text-tb-navy/50 hover:text-tb-navy p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 my-4">
            <button
              data-testid="classify-booking-pleasure"
              onClick={() => setTripType("pleasure")}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-1 tb-tap ${
                tripType === "pleasure"
                  ? "bg-tb-gold border-tb-gold text-tb-navy"
                  : "bg-tb-cream-2 border-tb-cream-3 text-tb-navy/70"
              }`}
            >
              <Compass className="w-6 h-6" />
              <span className="font-serif-it text-lg">Pleasure</span>
              <span className="font-hand text-xs opacity-70">files into journal</span>
            </button>
            <button
              data-testid="classify-booking-business"
              onClick={() => setTripType("business")}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-1 tb-tap ${
                tripType === "business"
                  ? "bg-tb-navy border-tb-navy text-tb-gold-bright"
                  : "bg-tb-cream-2 border-tb-cream-3 text-tb-navy/70"
              }`}
            >
              <Briefcase className="w-6 h-6" />
              <span className="font-serif-it text-lg">Business</span>
              <span className="font-hand text-xs opacity-70">kept out of chapters</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="block font-hand text-tb-navy/70 text-xs mb-1">Start date</span>
              <input
                data-testid="classify-booking-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
              />
            </label>
            <label>
              <span className="block font-hand text-tb-navy/70 text-xs mb-1">End date</span>
              <input
                data-testid="classify-booking-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
              />
            </label>
          </div>

          <label className="block mt-3">
            <span className="block font-hand text-tb-navy/70 text-xs mb-1">
              Budget (optional)
            </span>
            <input
              data-testid="classify-booking-budget"
              type="number"
              inputMode="decimal"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
            />
          </label>

          <div className="flex items-center gap-2 mt-6">
            <button
              data-testid="classify-booking-cancel"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy/70 font-hand"
            >
              cancel
            </button>
            <button
              data-testid="classify-booking-submit"
              onClick={onSubmit}
              disabled={saving}
              className="flex-1 py-2 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand disabled:opacity-50"
            >
              {saving ? "filing…" : `file as ${tripType}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
